import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Archive, MoreHorizontal, Pause, Pencil, Play, Trash2, X } from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "~/lib/api";
import {
    type TenantRecord,
    type TenantStatus,
    useDeleteTenant,
    useUpdateTenant,
} from "~/lib/tenants";

interface TenantRowActionsProps {
    tenant: TenantRecord;
}

const modalStyle: React.CSSProperties = {
    padding: "var(--space-6)",
};

export function TenantRowActions({ tenant }: TenantRowActionsProps): React.ReactElement {
    const [renameOpen, setRenameOpen] = useState(false);
    const [renameValue, setRenameValue] = useState(tenant.name);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const update = useUpdateTenant();
    const remove = useDeleteTenant();

    async function setStatus(next: TenantStatus): Promise<void> {
        try {
            await update.mutateAsync({ id: tenant.id, status: next });
            toast.success(`Tenant ${next}`);
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Failed to update tenant");
        }
    }

    async function rename(): Promise<void> {
        const v = renameValue.trim();
        if (!v) {
            toast.error("Name cannot be empty");
            return;
        }
        try {
            await update.mutateAsync({ id: tenant.id, name: v });
            toast.success("Renamed");
            setRenameOpen(false);
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Failed to rename");
        }
    }

    async function doDelete(): Promise<void> {
        try {
            await remove.mutateAsync(tenant.id);
            toast.success("Tenant deleted");
            setDeleteOpen(false);
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Failed to delete tenant");
        }
    }

    const blockDelete = tenant.userCount > 0;

    return (
        <>
            <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                    <button type="button" className="btn btn--ghost btn--sm" aria-label="Row actions">
                        <MoreHorizontal size={16} />
                    </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                    <DropdownMenu.Content
                        align="end"
                        sideOffset={4}
                        className="surface-raised enter-up z-popover"
                        style={{ minWidth: 200, padding: "var(--space-1)" }}
                    >
                        <DropdownMenu.Item
                            className="cmdbar__item"
                            onSelect={() => {
                                setRenameValue(tenant.name);
                                setRenameOpen(true);
                            }}
                        >
                            <Pencil size={14} aria-hidden />
                            <span>Rename…</span>
                        </DropdownMenu.Item>
                        {tenant.status === "active" ? (
                            <DropdownMenu.Item className="cmdbar__item" onSelect={() => void setStatus("suspended")}>
                                <Pause size={14} aria-hidden />
                                <span>Suspend</span>
                            </DropdownMenu.Item>
                        ) : (
                            <DropdownMenu.Item className="cmdbar__item" onSelect={() => void setStatus("active")}>
                                <Play size={14} aria-hidden />
                                <span>Reactivate</span>
                            </DropdownMenu.Item>
                        )}
                        {tenant.status !== "archived" && (
                            <DropdownMenu.Item className="cmdbar__item" onSelect={() => void setStatus("archived")}>
                                <Archive size={14} aria-hidden />
                                <span>Archive</span>
                            </DropdownMenu.Item>
                        )}
                        <DropdownMenu.Separator className="divider" style={{ margin: "var(--space-1) 0" }} />
                        <DropdownMenu.Item
                            className="cmdbar__item text-danger"
                            disabled={blockDelete}
                            onSelect={() => setDeleteOpen(true)}
                        >
                            <Trash2 size={14} aria-hidden />
                            <span>{blockDelete ? `Delete (${tenant.userCount} users)` : "Delete…"}</span>
                        </DropdownMenu.Item>
                    </DropdownMenu.Content>
                </DropdownMenu.Portal>
            </DropdownMenu.Root>

            {/* Rename modal */}
            <Dialog.Root open={renameOpen} onOpenChange={setRenameOpen}>
                <Dialog.Portal>
                    <Dialog.Overlay className="dialog-overlay">
                    <Dialog.Content className="dialog-content enter-up" style={modalStyle}>
                        <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                            <Dialog.Title className="type-h2">Rename tenant</Dialog.Title>
                            <Dialog.Close asChild>
                                <button type="button" className="btn btn--ghost btn--sm" aria-label="Close">
                                    <X size={14} />
                                </button>
                            </Dialog.Close>
                        </div>
                        <div className="stack" style={{ marginTop: "var(--space-4)" }}>
                            <label className="stack-xs">
                                <span className="text-12 text-text-muted">Name</span>
                                <input
                                    autoFocus
                                    className="input"
                                    value={renameValue}
                                    onChange={(e) => setRenameValue(e.target.value)}
                                />
                            </label>
                            <button
                                type="button"
                                className="btn btn--primary press-crush"
                                onClick={rename}
                                disabled={update.isPending}
                            >
                                {update.isPending ? "Saving…" : "Save"}
                            </button>
                        </div>
                    </Dialog.Content>
                    </Dialog.Overlay>
                </Dialog.Portal>
            </Dialog.Root>

            {/* Delete confirmation */}
            <Dialog.Root open={deleteOpen} onOpenChange={setDeleteOpen}>
                <Dialog.Portal>
                    <Dialog.Overlay className="dialog-overlay">
                    <Dialog.Content className="dialog-content enter-up" style={modalStyle}>
                        <Dialog.Title className="type-h2">Delete tenant?</Dialog.Title>
                        <Dialog.Description className="text-text-muted text-13" style={{ marginTop: "var(--space-2)" }}>
                            <strong className="text-text">{tenant.name}</strong> ({tenant.slug}) will be permanently
                            removed. This is irreversible. The audit log retains the historical record.
                        </Dialog.Description>
                        <div className="cluster-xs" style={{ marginTop: "var(--space-4)", justifyContent: "flex-end" }}>
                            <Dialog.Close asChild>
                                <button type="button" className="btn btn--ghost">Cancel</button>
                            </Dialog.Close>
                            <button
                                type="button"
                                className="btn btn--danger press-crush"
                                onClick={doDelete}
                                disabled={remove.isPending}
                            >
                                {remove.isPending ? "Deleting…" : "Delete"}
                            </button>
                        </div>
                    </Dialog.Content>
                    </Dialog.Overlay>
                </Dialog.Portal>
            </Dialog.Root>
        </>
    );
}
